import { TFunction } from 'react-i18next';
import { logEvent } from '@amplitude/analytics-browser';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '../../Typography';
import Button from '../../Button';
import { DownloadIcon, ShieldCheckIcon } from '../../../components/Icons';
import CopyToClipboardButton from '../../CopyToClipboardButton';
import { twoFactorEvents } from '../../../constants/events';
import { COLORS } from '../../../themev2';

interface Step2DownloadNewRecoveryCodeProps {
  t: TFunction;
  recoveryCode: string;
  handleFinish: () => void;
}

export default function Step2DownloadNewRecoveryCode({
  t,
  recoveryCode,
  handleFinish,
}: Step2DownloadNewRecoveryCodeProps) {
  return (
    <Grid container rowSpacing={3}>
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: '1rem' }}>
          {t('myAccount:security.generateRecoveryCodeModal.downloadNewRecoveryCodeTitle')}
        </Typography>
        <Divider sx={{ borderColor: COLORS.gray[900], marginTop: '1em' }} />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ textAlign: 'left', color: COLORS.gray[1100] }}>
          {t('myAccount:security.generateRecoveryCodeModal.downloadNewRecoveryCodeDescription')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1em',
        }}
      >
        <ShieldCheckIcon />
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '24px',
            lineHeight: '36px',
            background: 'linear-gradient(272.4deg, #0BAAA1 11.6%, #0B8DAA 87.19%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {t('myAccount:security.generateRecoveryCodeModal.downloadNewRecoveryCodeNewCodeTitle')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={8} md={6}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            backgroundColor: COLORS.brand.white,
            padding: '0.75rem 1rem',
            borderRadius: '6px',
            border: `1px solid ${COLORS.gray[700]}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'monospace',
              fontWeight: 500,
              fontSize: '14px',
              letterSpacing: '0.5px',
              color: COLORS.gray[1200],
            }}
          >
            {recoveryCode}
          </Typography>
        </Box>
      </Grid>
      <Grid
        item
        xs={12}
        sm={4}
        md={6}
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          flexDirection: 'row',
          alignItems: 'end',
          gap: '1rem',
        }}
      >
        <CopyToClipboardButton
          textToCopy={recoveryCode}
          onClick={() => {
            logEvent(twoFactorEvents.CLICK_COPY_RECOVERY_CODES);
          }}
          text={t('myAccount:security.twoFactorSetupModal.copyRecoveryCodes')}
        />
        <Button
          isFullHeight={false}
          type="outline"
          color="tertiary"
          onClick={() => {
            const element = document.createElement('a');
            const file = new Blob([recoveryCode], { type: 'text/plain' });
            element.href = URL.createObjectURL(file);
            element.download = `${t('myAccount:security.twoFactorSetupModal.recoveryCodesDocument')}.txt`;
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
            logEvent(twoFactorEvents.CLICK_DOWNLOAD_RECOVERY_CODES);
          }}
          startIcon={<DownloadIcon />}
        >
          {t('myAccount:security.twoFactorSetupModal.downloadRecoveryCodes')}
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button fullWidth color="secondary" onClick={handleFinish}>
          {t('myAccount:security.generateRecoveryCodeModal.done')}
        </Button>
      </Grid>
    </Grid>
  );
}
